@use '@/styles/variables' as *;

.header {
  position: sticky;
  top: 0;
  z-index: $z-header;
  height: $header-height;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 $section-padding-x;
  background-color: transparent;
  border-bottom: 1px solid transparent;
  transition:
    background-color 0.3s ease,
    border-color 0.3s ease;

  // backdrop-filter lives on ::before so it never becomes a containing block
  // for the position:fixed mobile nav overlay (backdrop-filter breaks fixed positioning)
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    will-change: backdrop-filter;
    backdrop-filter: blur(0.001px);
    transition: backdrop-filter 0.3s ease;
  }

  &.scrolled::before {
    backdrop-filter: blur(12px);
  }
}

.scrolled {
  background-color: color-mix(in oklch, var(--background) 15%, transparent);
  border-bottom-color: var(--border);

  @supports not (backdrop-filter: blur(1px)) {
    background-color: var(--background);
  }
}

.logo {
  font-weight: 700;
  font-size: 1.125rem;
  font-family: var(--font-display);
  letter-spacing: -0.02em;
  text-decoration: none;
  color: var(--foreground);
  display: inline-block;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);

  &:hover {
    transform: translateY(-2px);

    .logoDev {
      background-position: 100% 50%;
    }
  }
}

.logoDev {
  background: linear-gradient(135deg, var(--forest), var(--water), var(--forest));
  background-size: 200% 200%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-position: 0% 50%;
  transition: background-position 0.6s ease;
}

// ─── Desktop nav ──────────────────────────────────────────────────────────────

.nav {
  display: flex;
  gap: 2rem;
}

.navLink {
  text-decoration: none;
  font-size: 0.875rem;
  color: var(--muted-foreground);
  transition: color 0.2s ease;

  &:hover {
    color: var(--foreground);
  }
}

// ─── Actions ──────────────────────────────────────────────────────────────────

.actions {
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.langToggle {
  display: inline-flex;
  align-items: center;
  height: 2rem;
  padding: 0 0.625rem;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--muted-foreground);
  cursor: pointer;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--muted);
    border-color: var(--foreground);
  }
}

.themeToggle {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  color: var(--muted-foreground);
  cursor: pointer;
  border-radius: 0.5rem;
  transition:
    color 0.2s ease,
    background-color 0.2s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--muted);
  }
}

.sunIcon,
.moonIcon {
  position: absolute;
  width: 1.125rem;
  height: 1.125rem;
  transition:
    transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.35s ease;
}

// Light mode — moon visible (click to go dark), sun waiting below
.sunIcon {
  transform: translateY(120%);
  opacity: 0;
}

.moonIcon {
  transform: translateY(0);
  opacity: 1;
}

// Dark mode — sun visible (click to go light), moon waiting below
:global(.dark) .sunIcon {
  transform: translateY(0);
  opacity: 1;
}

:global(.dark) .moonIcon {
  transform: translateY(120%);
  opacity: 0;
}

// ─── Burger button (hidden on desktop) ────────────────────────────────────────

.menuToggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  color: var(--muted-foreground);
  cursor: pointer;
  border-radius: 0.5rem;
  transition:
    color 0.2s ease,
    background-color 0.2s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--muted);
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
}

// ─── Mobile ───────────────────────────────────────────────────────────────────

@media (max-width: #{$breakpoint-sm}) {
  .header {
    display: flex;
    justify-content: space-between;
  }

  // Mobile nav overlay — always in DOM, toggled via opacity/pointer-events
  .nav {
    position: fixed;
    top: $header-height;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: $z-nav-overlay;
    flex-direction: column;
    padding: 1.5rem $section-padding-x 3rem;
    gap: 0;
    background-color: color-mix(in oklch, var(--background) 94%, transparent);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    overflow-y: auto;

    opacity: 0;
    pointer-events: none;
    transform: translateY(-6px);
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;

    &.navOpen {
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0);
    }
  }

  .navLink {
    font-size: 1.75rem;
    font-family: var(--font-display);
    font-weight: 700;
    letter-spacing: -0.03em;
    color: var(--foreground);
    padding: 1rem 0;
    border-bottom: 1px solid var(--border);

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      color: var(--muted-foreground);
    }
  }

  .menuToggle {
    display: flex;
  }
}

// ─── Reduced motion ───────────────────────────────────────────────────────────

@media (prefers-reduced-motion: reduce) {
  .logo {
    transition: none;

    .logoDev {
      transition: none;
    }
  }

  .sunIcon,
  .moonIcon {
    transition: opacity 0.25s ease;
  }

  .nav {
    transition: opacity 0.15s ease;
  }
}
